// AI-generated. Reviewed by: —
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createInvitation } from '@/services/invitationService';
import { USER_ROLE_LABELS } from '@/types';
import type { UserRole } from '@/types';

const ALL_ROLES = Object.keys(USER_ROLE_LABELS) as UserRole[];

const inviteSchema = z.object({
  invitedEmail: z
    .string()
    .min(1, 'Email is required')
    .email('Must be a valid email')
    .refine((v) => v.endsWith('@cre8orglobal.com'), {
      message: 'Must be a @cre8orglobal.com address',
    }),
  firstName: z.string().min(1, 'First name is required').max(100),
  lastName: z.string().min(1, 'Last name is required').max(100),
  roles: z
    .array(z.enum(['Employee', 'Manager', 'Admin', 'PartnersMng', 'CatalogMng', 'OrdersMng']))
    .min(1, 'Select at least one role'),
});

type InviteFormData = z.infer<typeof inviteSchema>;

interface InviteUserModalProps {
  onClose: () => void;
}

export default function InviteUserModal({ onClose }: InviteUserModalProps) {
  const qc = useQueryClient();

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<InviteFormData>({
    resolver: zodResolver(inviteSchema),
    defaultValues: { invitedEmail: '', firstName: '', lastName: '', roles: ['Employee'] },
  });

  const invite = useMutation({
    mutationFn: (data: InviteFormData) =>
      createInvitation({
        invitedEmail: data.invitedEmail,
        firstName: data.firstName,
        lastName: data.lastName,
        role: data.roles[0],
        roles: data.roles,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invitations'] });
      onClose();
    },
  });

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      role="dialog"
      aria-modal="true"
      aria-label="Invite user"
    >
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
        <h2 className="text-lg font-semibold mb-4">Invite User</h2>

        <form onSubmit={handleSubmit((data) => invite.mutate(data))} noValidate>
          <div className="grid gap-3 text-sm">
            <div>
              <label htmlFor="invitedEmail" className="block font-medium mb-1">
                Email
              </label>
              <input
                id="invitedEmail"
                type="email"
                {...register('invitedEmail')}
                className="w-full border rounded px-3 py-1.5"
                aria-describedby={errors.invitedEmail ? 'invitedEmail-err' : undefined}
              />
              {errors.invitedEmail && (
                <p id="invitedEmail-err" className="text-red-500 mt-0.5">
                  {errors.invitedEmail.message}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="firstName" className="block font-medium mb-1">
                First Name
              </label>
              <input
                id="firstName"
                {...register('firstName')}
                className="w-full border rounded px-3 py-1.5"
                aria-describedby={errors.firstName ? 'firstName-err' : undefined}
              />
              {errors.firstName && (
                <p id="firstName-err" className="text-red-500 mt-0.5">
                  {errors.firstName.message}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="lastName" className="block font-medium mb-1">
                Last Name
              </label>
              <input
                id="lastName"
                {...register('lastName')}
                className="w-full border rounded px-3 py-1.5"
                aria-describedby={errors.lastName ? 'lastName-err' : undefined}
              />
              {errors.lastName && (
                <p id="lastName-err" className="text-red-500 mt-0.5">
                  {errors.lastName.message}
                </p>
              )}
            </div>

            <fieldset>
              <legend className="block font-medium mb-1">Roles</legend>
              <Controller
                name="roles"
                control={control}
                render={({ field }) => (
                  <div className="grid grid-cols-2 gap-1">
                    {ALL_ROLES.map((role) => (
                      <label key={role} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-brand-surface rounded px-1 py-0.5">
                        <input
                          type="checkbox"
                          checked={field.value.includes(role)}
                          onChange={(e) => {
                            const next = e.target.checked
                              ? [...field.value, role]
                              : field.value.filter((r) => r !== role);
                            field.onChange(next);
                          }}
                          className="accent-brand-accent"
                          aria-label={USER_ROLE_LABELS[role]}
                        />
                        {USER_ROLE_LABELS[role]}
                      </label>
                    ))}
                  </div>
                )}
              />
              {errors.roles && (
                <p className="text-red-500 mt-0.5">{errors.roles.message}</p>
              )}
            </fieldset>
          </div>

          {invite.isError && (
            <p className="text-red-500 text-sm mt-3" role="alert">
              Failed to send invitation. Please try again.
            </p>
          )}

          <div className="flex justify-end gap-2 mt-5">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-1.5 border rounded text-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={invite.isPending}
              className="px-4 py-1.5 bg-brand-accent text-brand-text-inverse rounded text-sm disabled:opacity-40"
            >
              {invite.isPending ? 'Sending…' : 'Send Invite'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
